import { Component, Input, OnDestroy } from '@angular/core';
import { LockedDayPermissions } from '../../../models/permissions/permission.models';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { ImplementerEditor, ImplementerSnapshotSummary } from '../../../models/implementer/implementer.models';
import { ImplementerService } from '../../../services/implementer/implementer.service';
import { ValidationErrorModel, ValidationResultModel } from '../../../_foundation/models/validation.models';
import { TimeZoneService } from '../../../services/time-zone/time-zone.service';
import { cloneDeep } from 'lodash';
import { PermissionsService } from '../../../services/permissions/permissions.service';
import { AppConfig } from '../../../app/app.config';
import { DateTime } from 'luxon';

declare var $: any;
@Component({
  selector: 'implementer-initialiser-component',
  templateUrl: 'implementer-initialiser-component.html'
})
export class ImplementerInitialiserComponent implements OnDestroy {
  @Input() dailyLogDate: PtLuxonDate;
  @Input() lockedDayPermissions: LockedDayPermissions;

  config = AppConfig.settings;

  loading: boolean;
  implementerLoading: boolean;
  validationResult: ValidationResultModel;

  editor: ImplementerEditor;
  snapshotSummary: ImplementerSnapshotSummary;
  isSjh: boolean;

  datesValid: boolean;
  private proxy: any;

  implementerLoadingText: string;
  implementerInitialiserValid: boolean;
  initialiserTimeStart: DateTime;

  constructor(
    private implementerService: ImplementerService,
    private timeZoneService: TimeZoneService,
    private permissionService: PermissionsService,
  ) {
    // sjh versions will choose a date range to unlock for editing, on the vessel they can only edit 1 day at a time
    this.isSjh = !this.config.isVesselEnvironment;
  }

  async ngOnInit() {
    this.loading = true;
    await this.startConnection();

    this.snapshotSummary = await this.implementerService.getActiveSnapshotSummary(this.config.isVesselEnvironment);

    // if lockedDay permissions have not been passed into this component (as they only get passed in, on daily log), we need to go fetch them
    if (!this.lockedDayPermissions) {
      this.lockedDayPermissions = await this.permissionService.lockedDayPermissions(this.dailyLogDate);
    }

    // no active snapshot summary means implementer is not active, the editor is what we will use to active implementer mode
    if (!this.snapshotSummary) {
      this.editor = await this.implementerService.getImplementerEditor(this.config.isVesselEnvironment);
      this.editor.environmentTypeId = this.config.isVesselEnvironment ? 2 : 1;
    }

    this.loading = false;
  }

  ngOnDestroy(): void {
    this.stopSignalRConnection();
  }

  async activateImplementer() {
    this.implementerLoading = true;
    this.resetValidation();
    this.initialiserTimeStart = DateTime.local();

    // when of vessel env there is no range, only the start date is editable, so we need to set the endDate ourselves or validation will trigger
    if (this.config.isVesselEnvironment) {
      this.editor.endDate = cloneDeep(this.editor.startDate);
    }

    await this.implementerService.activateImplementer(this.editor).then(async result => {
      this.snapshotSummary = result;
      this.stopSignalRConnection();
    }, error => {
      if (error.status && error.status === 412) {
        this.validationResult = error.error;
        this.validationResult.isValid = false;
        this.implementerInitialiserValid = false;
      }
      else {
        this.implementerInitialiserValid = false;
        if (error.error.exceptionMessage === 'Multiple active snapshots detected. Cancelling process.') {
          this.validationResult = new ValidationResultModel();
          this.validationResult.errors = [];
          this.validationResult.isValid = false;
          this.validationResult.errors.push({
            message: 'An error occurred, please re-try the activation process below. If error persists, contact support.',
            property: null
          });
        } else {
          this.validationResult = this.implementerService.buildFailureValidationResult(
            error,
            'An error occurred while activating implementer mode. Please try again. If the error persists, contact support.'
          );
        }
      }
    });

    this.implementerLoading = false;
  }

  async dateChanged(ptDate: PtLuxonDate) {
    if (ptDate.dateTime) {
      // datepickers are tied to ptDate.localDate, so on dateChange we need to get UTC date based on chosen localDate
      const tz = await this.timeZoneService.getEditorLocally({dateTime: ptDate.dateTime, dateAmbiguous: true});
      if (tz) { // tz might be null if user is manually typing in a time that is before app start time
        ptDate.setOffset(tz.secondsOffsetFromUtc, true);
      }
    } else {
      ptDate.dateTime = null;
    }
  }

  private async startConnection() {
    this.proxy = $.connection.ImplementerHub;

    this.proxy.client.BroadcastBackingUpDataMessage = ((name, message) => {
      this.implementerInitialiserValid = true;
      this.implementerLoadingText = message;
    });

    this.proxy.client.BroadcastRestoringDataToImplementerMessage = ((name, message) => {
      // if this gets called before at least 3 seconds have passed, then wait until 3 seconds since init time start to display the next message
      // otherwise the user won't have time to read the previous one
      if ((DateTime.local().toMillis() - this.initialiserTimeStart.toMillis()) / 1000 < 3) {
        setTimeout(() => {
          this.implementerLoadingText = message;
        }, 3000 - (DateTime.local().toMillis() - this.initialiserTimeStart.toMillis()));
      }
    });

    $.connection.hub.url = `${AppConfig.settings.apiUrl}api/signalr`;
    $.connection.hub.start({skipNegotiation: false, transport: ['serverSentEvents', 'longPolling']})
      .done(() => {
      }).fail(err => {
        console.log(err);
    });
  }

  private stopSignalRConnection() {
    $.connection.hub.stop();
  }

  private resetValidation() {
    this.validationResult = new ValidationResultModel();
    this.validationResult.isValid = true;
    this.validationResult.errors = [];
    this.datesValid = true;
  }
}



// WEBPACK FOOTER //
// ./src/pages/implementer/implementer-initialiser-component/implementer-initialiser-component.ts